import { type DashboardOfficeDetail, type DisplayViewConfig, type PairingPoll, type PairingStart } from '../models/dashboard.models';

// The physical display screen is unauthenticated (no Keycloak) — it identifies itself with a
// long-lived X-Display-Token issued during pairing, so these calls bypass core/utils/api.ts
// (which always injects a Keycloak bearer token) and talk to the API directly.

const baseUrl = (): string => process.env.REACT_APP_BASE_URL ?? '';

export const startPairing = async (): Promise<PairingStart | undefined> => {
  try {
    const response = await fetch(`${baseUrl()}/display/pairing/start`, { method: 'POST' });
    if (!response.ok) {
      return undefined;
    }
    return await response.json();
  } catch {
    return undefined;
  }
};

export const pollPairing = async (pollToken: string): Promise<PairingPoll | undefined> => {
  try {
    const response = await fetch(`${baseUrl()}/display/pairing/poll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pollToken }),
    });
    if (!response.ok) {
      return undefined;
    }
    return await response.json();
  } catch {
    return undefined;
  }
};

// A screen reaches the display endpoints one of two ways: as a paired device presenting its
// long-lived X-Display-Token, or — for displays an admin marked public — as an anonymous visitor
// who just opened the shared link, identified only by the display id in the URL. Both are
// unauthenticated as far as Keycloak is concerned; the backend decides what a bare id may read.
const displayRequestInit = (displayToken?: string): RequestInit | undefined =>
  (displayToken ? { headers: { 'X-Display-Token': displayToken } } : undefined);

const displayEndpoint = (path: string, displayToken?: string, displayId?: string): string =>
  (!displayToken && displayId ? `${baseUrl()}${path}?displayId=${encodeURIComponent(displayId)}` : `${baseUrl()}${path}`);

export const getDisplayViewConfig = async (
  displayToken?: string,
  displayId?: string,
): Promise<{ status: number; config?: DisplayViewConfig }> => {
  try {
    const response = await fetch(displayEndpoint('/display/view', displayToken, displayId), displayRequestInit(displayToken));
    if (!response.ok) {
      return { status: response.status };
    }
    return { status: response.status, config: await response.json() };
  } catch {
    return { status: -1 };
  }
};

export const getDisplayViewData = async (
  displayToken?: string,
  displayId?: string,
): Promise<{ status: number; data?: { displayId: string; data: DashboardOfficeDetail } }> => {
  try {
    const response = await fetch(displayEndpoint('/display/view/data', displayToken, displayId), displayRequestInit(displayToken));
    if (!response.ok) {
      return { status: response.status };
    }
    return { status: response.status, data: await response.json() };
  } catch {
    return { status: -1 };
  }
};
